import React from "react";
import { Link } from "react-router-dom";
import CopilotProjectForm, {
  FolderPlusIcon,
  ProjectFormSummary,
} from "./CopilotProjectForm";
// The frame this card used to define inline. Same component, same name — it
// just lives where the proposal cards can reach it too.
import Shell from "./CopilotCardShell";

/* The gate in front of anything the copilot does that changes their workspace.
 *
 * Every other turn here is reversible — a search can be ignored, an answer can
 * be wrong at no cost. This one is not: once it runs, something exists. So it
 * does not look like a chat message. It is a card that states the change and
 * waits.
 *
 * For actions with inputs the user should own, the card is a FORM (see
 * CopilotProjectForm): the model pre-fills what it heard, the user corrects and
 * confirms, and the edited values are what run. Anything else falls back to a
 * plain description with Approve / Cancel — phrased server-side, by the side
 * that knows what will actually happen.
 *
 * It never silently disappears. After a decision it stays, marked, because the
 * record of a change is part of the change.
 */

const LABELS = {
  create_project: "Create a project",
  import_tender: "Create a project from a tender",
};

const titleFor = (name) =>
  LABELS[name] || String(name || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const parseArgs = (action) => {
  try {
    return JSON.parse(action?.function?.arguments || "{}") || {};
  } catch {
    // Truncated argument stream. An empty form is the right fallback — better
    // than refusing to render the only way forward.
    return {};
  }
};

const ShieldIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l7 3v6c0 4.4-3 8.2-7 9-4-.8-7-4.6-7-9V6l7-3z" />
    <path d="M9.5 12.2l1.8 1.8 3.4-3.6" />
  </svg>
);

/* "superseded" is not a third choice the user made — it is what happens when
 * they type something else instead of answering. Labelled distinctly so the
 * transcript does not claim they cancelled something they simply walked past. */
const RESOLVED_LABEL = {
  approve: "Approved",
  deny: "Cancelled",
  superseded: "Not run — you moved on",
};

/* The card's real state, which is NOT the same as "the user clicked Approve".
 *
 * Pressing Create starts a request; it does not finish one. Labelling the card
 * "Created" on the click is a claim we have not been told is true — and when the
 * name turns out to be taken, that badge sits there contradicting the copilot's
 * own apology directly below it. So approval only gets us to "creating", and the
 * server's tool_completed frame decides which way it settles.
 */
const phaseOf = ({ resolved, created, failed }) => {
  if (!resolved) return null;
  if (resolved !== "approve") return resolved;
  if (failed) return "failed";
  if (created) return "created";
  return "creating";
};

const Pill = ({ tone, children }) => {
  const tones = {
    good: "bg-[#2DAD4E]/10 text-[#1F7D38]",
    bad: "bg-[#FEF2F2] text-[#B91C1C]",
    muted: "bg-[#18101C]/[0.05] text-[#6B6472]",
  };
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-semibold ${tones[tone]}`}>
      {children}
    </div>
  );
};

const TickIcon = () => (
  <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/* Where the project lives. Only carries the /org segment when there is a real
 * id — the route guard treats a literal "undefined" as a bad path and bounces
 * to the projects list, which would make the button look broken. */
const projectHref = ({ projectId, organizationId }) => {
  const org = organizationId && String(organizationId);
  const valid = org && org !== "undefined" && org !== "null";
  return valid
    ? `/rfp-projects/project/${projectId}/org/${org}/project-files`
    : `/rfp-projects/project/${projectId}/project-files`;
};

/* Footer of the project card, once the form has been submitted. */
const ProjectStatus = ({ phase, created, failed, onOpenProject }) => (
  <div className="px-4 pb-3.5">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {phase === "creating" && (
        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#6B6472]">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-[2px] border-[#A04A97]/25 border-t-[#A04A97] motion-reduce:animate-none" />
          Creating…
        </span>
      )}
      {phase === "created" && (
        <Pill tone="good">
          <TickIcon />
          Created
        </Pill>
      )}
      {phase === "failed" && (
        <Pill tone="bad">
          <CrossIcon />
          Not created
        </Pill>
      )}
      {(phase === "deny" || phase === "superseded") && (
        <Pill tone="muted">
          <CrossIcon />
          {phase === "deny" ? "Cancelled" : "Not created — you moved on"}
        </Pill>
      )}

      {/* A button, not a link, because it no longer navigates — it opens the
          project beside the conversation. Leaving this an anchor would promise
          a page change that does not happen, and "open in new tab" would take
          people somewhere the button never goes. The real navigation lives
          inside the panel, as an anchor, where it is honest. */}
      {phase === "created" && created?.projectId && (
        onOpenProject ? (
          <button
            type="button"
            data-sensa-keep-panel
            onClick={() =>
              onOpenProject({
                projectId: created.projectId,
                projectName: created.projectName,
              })
            }
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[#A04A97]/30 bg-white px-3 py-1.5 text-[13.5px] font-semibold text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
          >
            Open project
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h13M13 6l6 6-6 6" />
            </svg>
          </button>
        ) : (
          <Link
            to={projectHref(created)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[#A04A97]/30 bg-white px-3 py-1.5 text-[13.5px] font-semibold text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
          >
            Open project
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h13M13 6l6 6-6 6" />
            </svg>
          </Link>
        )
      )}
    </div>

    {phase === "failed" && failed && (
      <p role="alert" className="mt-1.5 text-[13px] leading-relaxed text-[#B91C1C]">{failed}</p>
    )}
  </div>
);

/* Footer for any other write tool — a plain settled state, no lifecycle. */
const Resolved = ({ resolved }) => {
  const approved = resolved === "approve";
  return (
    <div className="px-4 pb-3.5">
      <Pill tone={approved ? "good" : "muted"}>
        {approved ? <TickIcon /> : <CrossIcon />}
        {RESOLVED_LABEL[resolved] || "Cancelled"}
      </Pill>
    </div>
  );
};

const CopilotApprovalCard = ({ message, onRespond, onOpenProject }) => {
  const { actions = [], resolved, submitted, created, failed } = message;
  const phase = phaseOf(message);

  // The form path: exactly one action, and one we have a form for. A batch of
  // actions is reviewed, not edited — editing several at once is a different
  // interaction and this card does not pretend to be it.
  const single = actions.length === 1 ? actions[0] : null;
  const isProject = single?.function?.name === "create_project";

  if (isProject) {
    return (
      <Shell icon={<FolderPlusIcon />} title="New project">
        {resolved ? (
          <>
            {/* The server's name wins when we have it — the tool trims and
                normalises whitespace, so what exists may differ from what was
                typed, and the card must show what exists. */}
            <ProjectFormSummary
              values={{
                ...(submitted || parseArgs(single)),
                ...(created?.projectName ? { name: created.projectName } : {}),
              }}
            />
            <ProjectStatus phase={phase} created={created} failed={failed} onOpenProject={onOpenProject} />
          </>
        ) : (
          <CopilotProjectForm
            uid={message.id}
            initial={parseArgs(single)}
            onCreate={(values) => onRespond?.("approve", { [single.id]: values }, values)}
            onCancel={() => onRespond?.("deny")}
          />
        )}
      </Shell>
    );
  }

  // A tender import is a project creation too, so once approved it settles
  // exactly like one: Creating…, then Created with a button to open it. There
  // is nothing to edit — the project is named after the tender — so the
  // server's sentence naming the tender is the whole of what is being agreed.
  if (single?.function?.name === "import_tender") {
    return (
      <Shell icon={<FolderPlusIcon />} title="New project from a tender">
        <div className="px-4 py-3.5">
          {single.detail && (
            <p className="break-words text-[14px] leading-relaxed text-[#18101C]">
              {single.detail.charAt(0).toUpperCase() + single.detail.slice(1)}.
            </p>
          )}
          <p className="mt-1 text-[13px] leading-relaxed text-[#6B6472]">
            The documents download in the background and take a few minutes.
          </p>
          {!resolved && (
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => onRespond?.("approve")}
                className="rounded-lg bg-[#A04A97] px-3.5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
              >
                Create project
              </button>
              <button
                type="button"
                onClick={() => onRespond?.("deny")}
                className="rounded-lg border border-[#18101C]/12 px-3.5 py-2 text-[13.5px] font-semibold text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.04] hover:text-[#3F3F46] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        {resolved && (
          <ProjectStatus phase={phase} created={created} failed={failed} onOpenProject={onOpenProject} />
        )}
      </Shell>
    );
  }

  const multiple = actions.length > 1;
  return (
    <Shell icon={<ShieldIcon />} title={multiple ? "Approve these actions" : "Approve this action"}>
      <div className="px-4 py-3.5">
        <ul className="space-y-2.5">
          {actions.map((a, i) => (
            <li key={a?.id || i} className="flex items-start gap-2.5">
              <span className="mt-[6px] h-1.5 w-1.5 flex-none rounded-full bg-[#A04A97]" />
              <div className="min-w-0">
                <p className="text-[14.5px] font-semibold leading-snug text-[#18101C]">
                  {titleFor(a?.function?.name)}
                </p>
                {a?.detail && (
                  <p className="mt-0.5 break-words text-[13.5px] leading-relaxed text-[#6B6472]">
                    {a.detail}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>

        {!resolved && (
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => onRespond?.("approve")}
              className="rounded-lg bg-[#A04A97] px-3.5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => onRespond?.("deny")}
              className="rounded-lg border border-[#18101C]/12 px-3.5 py-2 text-[13.5px] font-semibold text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.04] hover:text-[#3F3F46] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
            >
              Cancel
            </button>
            <span className="ml-auto text-[12px] text-[#77707E]">
              Nothing happens until you approve
            </span>
          </div>
        )}
      </div>
      {resolved && <Resolved resolved={resolved} created={created} />}
    </Shell>
  );
};

export default CopilotApprovalCard;
